import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Save, Loader2, RefreshCw, Sparkles, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/** Admin-only page for rotating the Perplexity API key (audit console). */
export default function AuditConsoleConfigAdmin() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit-console/config");
      setInfo(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey.trim() || apiKey.trim().length < 8) return toast.error("Key looks too short.");
    setSaving(true);
    try {
      await api.put("/admin/audit-console/config", { api_key: apiKey.trim() });
      toast.success("Saved. Audit Console is now live.");
      setApiKey("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="audit-console-config-page">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-neutral-900 flex items-center gap-3">
          <Sparkles className="w-7 h-7 text-[#E24E1B]" /> Audit Console · Perplexity key
        </h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl leading-relaxed">
          The AI Visibility Audit Console queries Perplexity's <strong>Sonar</strong> web-connected
          model to run real searches. Grab a free key at{" "}
          <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">
            perplexity.ai/settings/api <ExternalLink className="w-3 h-3" />
          </a>.
          Free tier = 5 requests/minute (1 audit per 2 minutes). Paid $5/mo = 60/min.
        </p>
      </div>

      <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="audit-console-config-active">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Currently active</div>
          <button onClick={load} disabled={loading} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : info ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="Source" value={info.source === "db" ? "🟢 DB override" : info.source === "env" ? "🟡 Env fallback" : "🔴 Not configured"} />
            <Row label="Configured" value={info.configured ? "Yes" : "No"} />
            <Row label="Key suffix" value={info.key_last4 ? `…${info.key_last4}` : "—"} mono />
            <Row label="Model" value={info.model} mono />
          </div>
        ) : null}
      </div>

      <form onSubmit={save} className="bg-white border border-neutral-200 p-6" data-testid="audit-console-config-form">
        <label className="block mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">New API key</div>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="pplx-..."
            className="lf-input font-mono w-full"
            data-testid="audit-console-config-input"
            autoComplete="off"
          />
        </label>
        <button type="submit" disabled={saving} className="lf-btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save credentials"}
        </button>
      </form>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm text-neutral-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
